<template>
  <div class="signup-overlay" :class="overlayClass">
    <div class="signup-modal" :class="cardClass">
      <!-- Header — el slot 'header' permite reemplazar la barra estándar
           por completo (lo usa el wizard para la pantalla de waitlist).
           Si no se pasa el slot, se renderiza la barra azul estándar. -->
      <slot name="header">
        <div class="signup-modal__header">
          <div class="signup-modal__header-left">
            <span class="signup-modal__header-title">{{ title }}</span>
            <span v-if="stepIndicator" class="signup-modal__header-step">{{ stepIndicator }}</span>
          </div>
          <div class="signup-modal__header-right">
            <slot name="header-right"></slot>
            <button v-if="showClose" class="signup-modal__close" @click="$emit('close')">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none"><path d="M6 6L16 16M16 6L6 16" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>
            </button>
          </div>
        </div>
      </slot>

      <!-- Body desplazable -->
      <div class="signup-modal__body">
        <div class="signup-modal__content">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SignUpModalShell",
  props: {
    // 'scrim'   → overlay oscuro difuminado (wizard sobre el login).
    // 'surface' → fondo limpio --kiey-blue-soft, sin scrim ni blur
    //             (ruta de referral, sin página detrás).
    backdrop: {
      type: String,
      default: 'scrim',
      validator: (v) => ['scrim', 'surface'].includes(v),
    },
    // Título de la barra azul del header. Vacío → sin header estándar.
    title: { type: String, default: '' },
    // Indicador de paso opcional (ej. "Step 2 of 4"). Lo usa el wizard.
    stepIndicator: { type: String, default: '' },
    // Botón de cierre opcional en el header. Oculto para la ruta referral.
    showClose: { type: Boolean, default: false },
    // Clase extra para la card (ej. modificador de waitlist del wizard).
    cardClass: { type: [String, Array, Object], default: '' },
    // Cuando true, el shell se teletransporta a document.body en mounted
    // para que position: fixed cubra el viewport (v-main crea un bloque
    // contenedor que atraparía el fixed detrás del app-bar).
    teleport: { type: Boolean, default: false },
  },
  computed: {
    overlayClass() {
      return `signup-overlay--${this.backdrop}`;
    },
  },
  mounted() {
    if (this.teleport && this.$el && this.$el.nodeType === 1) {
      document.body.appendChild(this.$el);
    }
  },
  beforeDestroy() {
    if (this.teleport && this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
};
</script>

<style>
/* El dropdown de Google Places debe quedar por encima del overlay
   (z-index 9999). Es global a propósito — el .pac-container lo inserta
   Google en document.body, fuera del scope del componente. */
.pac-container {
  z-index: 10001 !important;
}
</style>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* ── Design tokens (scoped al flujo de signup) ─────────── */
.signup-overlay,
.signup-modal {
  --kiey-navy: #0C2454;
  --kiey-header: #0A317C;
  --kiey-blue: #2F6FED;
  --kiey-blue-dark: #255ED3;
  --kiey-blue-soft: #F5F9FF;
  --kiey-blue-border: #AFC2EB;

  --surface-panel: #FFFFFF;
  --surface-soft: #F8FAFC;
  --surface-tint: #EEF4FF;

  --text-primary: #0C2454;
  --text-secondary: #5E6B85;
  --text-muted: #98A2B3;
  --text-link: #2F6FED;

  --border-default: #D9E1F0;
  --border-soft: #E6ECF5;
  --border-strong: #C7D2E0;

  --shadow-modal: 0 20px 60px rgba(8, 30, 74, 0.14);
  --shadow-soft: 0 8px 24px rgba(31, 67, 145, 0.08);
  --shadow-button: 0 4px 12px rgba(47, 111, 237, 0.20);

  --radius-modal: 20px;
  --radius-card: 12px;
  --radius-input: 10px;
  --radius-button: 10px;
}

/* ── Overlay ────────────────────────────────────────────── */
.signup-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 'scrim' — el look actual del wizard: overlay oscuro difuminado. */
.signup-overlay--scrim {
  background: rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* 'surface' — fondo branded limpio, sin scrim ni blur.
   Igual que el 'scrim' del wizard es un overlay fixed que cubre el
   viewport, pero sin el scrim oscuro: fondo branded --kiey-blue-soft.
   El overlay mismo es el contenedor de scroll (overflow-y: auto) y
   alinea la card al tope (align-items: flex-start) para que una card
   más alta que el viewport sea alcanzable scrolleando desde arriba en
   lugar de quedar recortada por el centrado. Necesita teleport para
   escapar del bloque contenedor que crea v-main. */
.signup-overlay--surface {
  align-items: flex-start;
  overflow-y: auto;
  background: var(--kiey-blue-soft);
  padding: 16px;
}

/* En 'surface' la card no se limita en altura: crece con su contenido.
   'margin: auto' la centra verticalmente cuando sobra espacio, y colapsa
   a flush-start (scrolleando el overlay) cuando es más alta que el
   viewport. */
.signup-overlay--surface .signup-modal {
  max-height: none;
  margin: auto;
}

/* En 'surface' el body no scrollea internamente — crece toda la card y
   el OVERLAY es el que scrollea. */
.signup-overlay--surface .signup-modal__body {
  overflow-y: visible;
}

/* ── Modal ──────────────────────────────────────────────── */
.signup-modal {
  width: 520px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: #FFFFFF;
  border: 1px solid #dfe6f2;
  border-radius: 24px;
  box-shadow: 0 24px 56px rgba(28, 51, 102, 0.10), 0 8px 18px rgba(28, 51, 102, 0.06);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ── Header ─────────────────────────────────────────────── */
.signup-modal__header {
  background: #2f6fed;
  min-height: 52px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.signup-modal__header-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.signup-modal__header-title {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
  color: #FFFFFF;
  margin-bottom: 0;
}

.signup-modal__header-step {
  font-size: 12px;
  line-height: 1.2;
  color: rgba(255, 255, 255, 0.80);
  font-weight: 600;
}

.signup-modal__header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.signup-modal__close {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 180ms ease, transform 180ms ease;
  flex-shrink: 0;
  box-shadow: none;
  color: #FFFFFF;
}

.signup-modal__close svg {
  opacity: 0.95;
  width: 16px;
  height: 16px;
}

.signup-modal__close:hover {
  background: rgba(255, 255, 255, 0.20);
  transform: scale(1.02);
}

/* ── Body ───────────────────────────────────────────────── */
.signup-modal__body {
  padding: 22px 24px 18px;
  overflow-y: auto;
  flex: 1;
}

.signup-modal__content {
  max-width: 520px;
  margin: 0 auto;
}

/* ── Variante waitlist (la pasa el wizard vía cardClass) ──── */
/* La card cambia de chrome: sin header azul, franja de acento + botón
   de cierre claro absoluto (esos elementos los inyecta el wizard en el
   slot #header). Acá sólo va lo que toca a la card y al body del shell. */
.signup-modal--waitlist {
  max-width: 520px;
  width: 92%;
  background: #FFFFFF;
  border: 1px solid #EEF2F7;
  border-radius: 24px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.12);
  overflow: hidden;
  padding: 0;
  position: relative;
}

.signup-modal--waitlist .signup-modal__body {
  padding: 20px 24px 0;
}

@media (max-width: 600px) {
  .signup-modal--waitlist {
    max-width: calc(100vw - 24px);
    border-radius: 22px;
  }

  .signup-modal--waitlist .signup-modal__body {
    padding: 20px 18px 0;
  }
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 600px) {
  .signup-modal {
    max-height: calc(100dvh - 24px);
    max-width: calc(100vw - 24px);
    border-radius: 20px;
  }

  /* 'surface' nunca re-limita la altura de la card — sigue creciendo
     y la página scrollea (override del max-height de arriba). */
  .signup-overlay--surface .signup-modal {
    max-height: none;
  }

  .signup-modal__header {
    min-height: 48px;
    padding: 10px 14px;
  }

  .signup-modal__header-title {
    font-size: 14px;
  }

  .signup-modal__header-right {
    gap: 6px;
  }

  .signup-modal__body {
    padding: 14px 16px 12px;
  }
}

@media (max-width: 380px) {
  .signup-modal {
    border-radius: 16px;
    max-width: calc(100vw - 16px);
    max-height: calc(100dvh - 16px);
  }

  /* 'surface' nunca re-limita la altura de la card (override del
     max-height de arriba). */
  .signup-overlay--surface .signup-modal {
    max-height: none;
  }

  .signup-modal__body {
    padding: 12px 14px 10px;
  }
}

@media (max-height: 720px) {
  .signup-modal__body {
    padding: 12px 20px 10px;
  }
}
</style>
